import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    FaHome,
    FaFileInvoiceDollar,
    FaBoxes,
    FaCogs,
    FaRocket,
    FaTools,
    FaMicrochip,
    FaChartLine,
    FaChevronRight,
    FaExternalLinkAlt,
    FaCommentDots,
    FaShoppingCart,
} from 'react-icons/fa';
import { getVisibleGroups } from '../config/navCatalog';

// Icon name (from navCatalog) -> component
const ICONS = {
    FaFileInvoiceDollar: FaFileInvoiceDollar,
    FaRocket: FaRocket,
    FaBoxes: FaBoxes,
    FaMicrochip: FaMicrochip,
    FaTools: FaTools,
    FaCogs: FaCogs,
    FaCommentDots: FaCommentDots,
    FaShoppingCart: FaShoppingCart,
};

const Sidebar = () => {
    const { userInfo } = useSelector((state) => state.auth);
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [openGroup, setOpenGroup] = useState(null);

    // Open the mobile drawer when the Header hamburger dispatches the event.
    useEffect(() => {
        const open = () => setMobileOpen(true);
        window.addEventListener('toggle-mobile-nav', open);
        return () => window.removeEventListener('toggle-mobile-nav', open);
    }, []);

    // Close the drawer on navigation.
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const isActive = (path) => {
        if (path === '/' && location.pathname !== '/') return false;
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    // Role-filtered groups come from the shared catalog, so the menu and the
    // Ctrl+K palette always agree on what this user can reach.
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('en') ? 'en' : 'th';
    const groups = userInfo ? getVisibleGroups(userInfo, lang) : [];

    // Keep the group containing the current page expanded.
    useEffect(() => {
        const match = groups.find((g) => g.items.some((it) => isActive(it.path)));
        if (match) setOpenGroup(match.key);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, userInfo]);

    const homeLink = userInfo
        ? { path: '/dashboard', label: t('nav.home'), Icon: FaChartLine }
        : { path: '/', label: t('nav.home'), Icon: FaHome };

    // ---- Shared: the list of groups, rendered for drawer and desktop flyout ----
    const renderGroupList = (onNavigate) => (
        <>
            <Link
                to={homeLink.path}
                onClick={onNavigate}
                className={`flex items-center gap-4 mx-3 px-4 h-[50px] rounded-2xl transition-colors ${
                    isActive(homeLink.path)
                        ? 'bg-[#f1f5f9] dark:bg-zinc-800 text-slate-900 dark:text-white font-bold'
                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-zinc-900/50'
                }`}
            >
                <homeLink.Icon size={20} className="flex-shrink-0" />
                <span className="text-[15px] whitespace-nowrap">{homeLink.label}</span>
            </Link>

            {groups.map((g) => {
                const Icon = ICONS[g.icon] || FaCogs;
                const expanded = openGroup === g.key;
                const groupActive = g.items.some((it) => isActive(it.path));
                return (
                    <div key={g.key} className="mt-1">
                        <button
                            onClick={() => setOpenGroup(expanded ? null : g.key)}
                            className={`w-full flex items-center gap-4 mx-3 px-4 h-[46px] rounded-2xl transition-colors ${
                                groupActive
                                    ? 'text-slate-900 dark:text-white font-bold'
                                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-zinc-900/50'
                            }`}
                            style={{ width: 'calc(100% - 1.5rem)' }}
                        >
                            <Icon size={20} className="flex-shrink-0" />
                            <span className="text-[15px] whitespace-nowrap flex-1 text-left">{g.label}</span>
                            <FaChevronRight
                                size={11}
                                className={`transition-transform ${expanded ? 'rotate-90' : ''}`}
                            />
                        </button>

                        {expanded && (
                            <div className="mt-1 mb-2 ml-8 mr-3 border-l border-slate-200 dark:border-zinc-700 pl-3 flex flex-col gap-0.5">
                                {g.items.map((it, idx) => {
                                    // หัวข้อย่อยในกลุ่ม (sub ใน navCatalog) — แสดงเมื่อเปลี่ยนหัวข้อ
                                    const heading = it.sub && it.sub !== g.items[idx - 1]?.sub ? (
                                        <div
                                            key={`h-${it.sub}`}
                                            className={`px-3 ${idx === 0 ? 'pt-1' : 'pt-3'} pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400`}
                                        >
                                            {it.sub}
                                        </div>
                                    ) : null;
                                    const isExternal = /^https?:\/\//.test(it.path);
                                    const itemClassName = `px-3 py-2 rounded-lg text-[13px] transition-colors ${
                                        isActive(it.path)
                                            ? 'bg-[#f1f5f9] dark:bg-zinc-800 text-slate-900 dark:text-white font-semibold'
                                            : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-zinc-900/50 hover:text-slate-800'
                                    }`;
                                    if (isExternal) {
                                        return (
                                            <React.Fragment key={it.path}>
                                            {heading}
                                            <a
                                                key={it.path}
                                                href={it.path}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={onNavigate}
                                                className={`${itemClassName} flex items-center justify-between gap-1`}
                                            >
                                                {it.label}
                                                <FaExternalLinkAlt size={9} className="opacity-50 flex-shrink-0" />
                                            </a>
                                            </React.Fragment>
                                        );
                                    }
                                    return (
                                        <React.Fragment key={it.path}>
                                            {heading}
                                            <Link
                                                to={it.path}
                                                onClick={onNavigate}
                                                className={itemClassName}
                                            >
                                                {it.label}
                                            </Link>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </>
    );

    return (
      <>
        {/* ===== Mobile Drawer (lg:hidden) ===== */}
        <div className={`lg:hidden fixed inset-0 z-[10000] ${mobileOpen ? '' : 'pointer-events-none'}`} aria-hidden={!mobileOpen}>
            <div
                onClick={() => setMobileOpen(false)}
                className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${mobileOpen ? 'opacity-100' : 'opacity-0'}`}
            />
            <div className={`absolute top-0 left-0 h-full w-[280px] max-w-[85vw] bg-white dark:bg-[#1c1c1e] shadow-2xl border-r border-slate-200 dark:border-[#38383a] flex flex-col transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="h-[64px] flex items-center justify-between px-4 border-b border-slate-100 dark:border-zinc-800">
                    <img src="/image/Pawin_Logo_long_transparent.png" alt="PAWIN" className="h-8 object-contain" />
                    <button onClick={() => setMobileOpen(false)} className="w-9 h-9 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-zinc-800" aria-label="Close menu">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto py-4">
                    {renderGroupList(() => setMobileOpen(false))}
                </div>
                {userInfo && (
                    <Link to="/profile" onClick={() => setMobileOpen(false)} className="p-4 border-t border-slate-100 dark:border-zinc-800 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-zinc-900/50 text-decoration-none">
                        <div className="w-11 h-11 rounded-full bg-gradient-to-tr from-slate-200 to-slate-100 dark:from-zinc-800 dark:to-zinc-700 flex items-center justify-center text-slate-800 dark:text-slate-200 font-black text-lg flex-shrink-0">
                            {userInfo.name ? userInfo.name.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div className="flex flex-col min-w-0">
                            <span className="text-[15px] font-bold text-slate-900 dark:text-slate-100 truncate">{userInfo.name || userInfo.email}</span>
                            <span className="text-[12px] text-slate-500 dark:text-zinc-500 truncate">{userInfo.isAdmin ? 'Administrator' : userInfo.isStaff ? 'Staff Member' : 'User'}</span>
                        </div>
                    </Link>
                )}
            </div>
        </div>

        {/* ===== Desktop rail: icons only, expands on hover ===== */}
        <div className="hidden lg:flex w-[80px] hover:w-[268px] flex-shrink-0 bg-white/90 dark:bg-[#1c1c1e]/90 backdrop-blur-2xl border-r border-slate-200 dark:border-[#38383a] flex-col h-screen sticky top-0 transition-all duration-300 group z-[9999] overflow-hidden shadow-sm">
            {/* Logo Area */}
            <div className="h-[72px] flex items-center justify-center border-b border-slate-100 dark:border-zinc-800 flex-shrink-0">
                <Link to="/" className="flex items-center justify-center w-full h-full">
                    <img
                        src="/image/Pawin_Logo_transparent.png"
                        alt="PAWIN Logo"
                        className="h-10 object-contain group-hover:hidden transition-all"
                    />
                    <img
                        src="/image/Pawin_Logo_long_transparent.png"
                        alt="PAWIN Logo Full"
                        className="h-8 object-contain hidden group-hover:block transition-all px-4"
                    />
                </Link>
            </div>

            {/* Collapsed: icon-only column. Expanded: full grouped menu. */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden py-4">
                {/* icons shown while collapsed */}
                <div className="group-hover:hidden flex flex-col items-center gap-2">
                    <Link
                        to={homeLink.path}
                        title={homeLink.label}
                        className={`w-[50px] h-[50px] rounded-2xl flex items-center justify-center transition-colors ${
                            isActive(homeLink.path) ? 'bg-[#f1f5f9] dark:bg-zinc-800 text-slate-900 dark:text-white' : 'text-slate-500'
                        }`}
                    >
                        <homeLink.Icon size={22} />
                    </Link>
                    {groups.map((g) => {
                        const Icon = ICONS[g.icon] || FaCogs;
                        const groupActive = g.items.some((it) => isActive(it.path));
                        return (
                            <div
                                key={g.key}
                                title={g.label}
                                className={`w-[50px] h-[50px] rounded-2xl flex items-center justify-center ${
                                    groupActive ? 'bg-[#f1f5f9] dark:bg-zinc-800 text-slate-900 dark:text-white' : 'text-slate-500'
                                }`}
                            >
                                <Icon size={22} />
                            </div>
                        );
                    })}
                </div>

                {/* full menu shown while hovered */}
                <div className="hidden group-hover:block">
                    {renderGroupList(undefined)}
                </div>
            </div>

            {/* Bottom User Area */}
            {userInfo && (
                <Link to="/profile" className="p-4 border-t border-slate-100 dark:border-zinc-800 flex items-center justify-center group-hover:justify-start gap-3 transition-all duration-300 cursor-pointer hover:bg-slate-50 dark:hover:bg-zinc-900/50 text-decoration-none flex-shrink-0">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-tr from-slate-200 to-slate-100 dark:from-zinc-800 dark:to-zinc-700 flex items-center justify-center text-slate-800 dark:text-slate-200 font-black text-lg flex-shrink-0 shadow-[inset_0_2px_4px_rgba(255,255,255,0.6)] border border-slate-200 dark:border-zinc-700">
                        {userInfo.name ? userInfo.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div className="hidden group-hover:flex flex-col min-w-0">
                        <span className="text-[15px] font-bold text-slate-900 dark:text-slate-100 truncate">
                            {userInfo.name || userInfo.email}
                        </span>
                        <span className="text-[12px] text-slate-500 dark:text-zinc-500 truncate font-medium tracking-wide">
                            {userInfo.isAdmin ? 'Administrator' : userInfo.isStaff ? 'Staff Member' : 'User'}
                        </span>
                    </div>
                </Link>
            )}
        </div>
      </>
    );
};

export default Sidebar;
